// The Marketing › Contacts roster: query planning and owner-facing wording.
// Pure module, no IO — unit-tested from src/test/contacts.test.ts. The roster
// reads v_customer_invite_status (v_customer_marketing + invite history), so
// every status word here maps to a real column; nothing is inferred.

import type {
  ConsentEvent,
  ConsentMethod,
  ContactListRow,
  ContactSource,
  MessageKind,
  SmsMessage,
} from "@/types/sms";

export const CONTACTS_PAGE_SIZE = 50;

/** The valid-NANP-mobile rule the roster and every tile count apply. */
export const VALID_MOBILE_PATTERN = "^[2-9][0-9]{2}[2-9][0-9]{6}$";

/** The view's stored copy of VALID_MOBILE_PATTERN (customer_order_stats.valid_mobile).
 *  Filtering on it rather than the regex gives the planner an exact row estimate. */
export const VALID_MOBILE_COLUMN = "valid_mobile";

/** "excluded" = the owner turned off marketing texts for them (sms_contacts.marketing_excluded_at). */
export type ContactStatusFilter = "all" | "opted_in" | "opted_out" | "unknown" | "invitable" | "excluded";
/** "orders" = no sms_contacts row (the customer only exists through the POS relay). */
export type ContactSourceFilter = "all" | "orders" | ContactSource;
export type ContactSortKey = "last_order_at" | "display_name" | "opt_in_status";

export interface ContactsSort {
  key: ContactSortKey;
  direction: "asc" | "desc";
}

export const DEFAULT_CONTACTS_SORT: ContactsSort = { key: "last_order_at", direction: "desc" };

/** v_customer_invite_status's history-aware invitable flag: is_invitable under
 *  greatest(invite_count, history_invite_count), the rule every invite path uses. */
export const INVITABLE_NOW_COLUMN = "is_invitable_now";

export interface ContactsQueryParams {
  term: string;
  status: ContactStatusFilter;
  source: ContactSourceFilter;
  sort: ContactsSort;
  page: number;
}

/** The PostgREST pieces the roster query applies, in order. Planned here so the
 *  escaping and paging are testable without a client. */
export interface ContactsQueryPlan {
  eq: [column: string, value: string | number | boolean][];
  /** customer_phone IN (…): the "excluded" filter, from the store's excluded phones. */
  inPhones: string[] | null;
  /** customer_phone NOT IN (…): the "manual" source filter leaves out rows created only to hold an exclusion. */
  notInPhones: string[] | null;
  isNull: string[];
  /** The phone regex, only when the view predates valid_mobile. */
  match: [column: string, pattern: string] | null;
  ilike: [column: string, pattern: string] | null;
  like: [column: string, pattern: string] | null;
  or: string | null;
  order: { column: string; ascending: boolean; nullsFirst: boolean }[];
  range: [from: number, to: number];
}

export interface ParsedSearch {
  /** Name fragment: letters, spaces, apostrophes, periods and hyphens only. */
  text: string | null;
  /** Phone fragment as bare digits (a leading US 1 dropped from an 11-digit entry). */
  digits: string | null;
}

/**
 * Splits a search box entry into a name part and a phone part. Everything that
 * is PostgREST syntax (`,` `(` `)` `"` `*` `%` `\`) is stripped from the name
 * part, so a typed term can never widen or break the filter.
 */
export function parseContactSearch(raw: string): ParsedSearch {
  const text = raw
    .replace(/[^\p{L}\p{M}\s'.-]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
  let digits = raw.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  return {
    text: /\p{L}/u.test(text) && text.length >= 2 ? text : null,
    digits: digits.length >= 3 ? digits : null,
  };
}

export interface ContactsQueryOptions {
  /** The view predates is_invitable_now: count only never-invited phones as
   *  invitable, so a customer with invite history is never shown as eligible. */
  legacyInvitable?: boolean;
  /** The view predates valid_mobile: match the phone against the pattern instead. */
  legacyValidMobile?: boolean;
  /** Every phone the owner excluded from marketing texts (fetchMarketingExclusions). The
   *  view does not carry the flag, so the "excluded" filter lists these phones. */
  excludedPhones?: string[];
  /** Every phone whose contact row sms_set_marketing_exclusion() created (source manual,
   *  source_ref marketing_exclusion). describeSource labels them "Orders (POS)", so the
   *  source filters list them there and not under "Added by staff". The view does not
   *  carry source_ref. */
  exclusionCreatedPhones?: string[];
}

export function buildContactsQuery(params: ContactsQueryParams, options: ContactsQueryOptions = {}): ContactsQueryPlan {
  const eq: ContactsQueryPlan["eq"] = options.legacyValidMobile ? [] : [[VALID_MOBILE_COLUMN, true]];
  let inPhones: string[] | null = null;
  // Soft-deleted contacts stay hidden even when opted out: deletion wins in the
  // roster, and the consent record itself is retained in sms_consent_events.
  const isNull = ["deleted_at"];

  if (params.status === "invitable") {
    if (options.legacyInvitable) eq.push(["is_invitable", true], ["history_invite_count", 0]);
    else eq.push([INVITABLE_NOW_COLUMN, true]);
  } else if (params.status === "excluded") {
    inPhones = [...(options.excludedPhones ?? [])].sort();
  } else if (params.status !== "all") eq.push(["opt_in_status", params.status]);

  const exclusionCreated = [...(options.exclusionCreatedPhones ?? [])].sort();
  let notInPhones: string[] | null = null;
  let sourceOr: string | null = null;
  if (params.source === "orders") {
    if (exclusionCreated.length > 0) sourceOr = `source.is.null,customer_phone.in.(${exclusionCreated.join(",")})`;
    else isNull.push("source");
  } else if (params.source !== "all") {
    eq.push(["source", params.source]);
    if (params.source === "manual" && exclusionCreated.length > 0) notInPhones = exclusionCreated;
  }

  const { text, digits } = parseContactSearch(params.term);
  let ilike: ContactsQueryPlan["ilike"] = null;
  let like: ContactsQueryPlan["like"] = null;
  let searchOr: string | null = null;
  if (text && digits) {
    searchOr = `display_name.ilike."*${text}*",customer_phone.like."*${digits}*"`;
  } else if (text) {
    ilike = ["display_name", `%${text}%`];
  } else if (digits) {
    like = ["customer_phone", `%${digits}%`];
  }

  const or = sourceOr && searchOr ? `and(or(${sourceOr}),or(${searchOr}))` : (sourceOr ?? searchOr);

  const page = Math.max(0, Math.floor(params.page));
  const from = page * CONTACTS_PAGE_SIZE;

  return {
    eq,
    inPhones,
    notInPhones,
    isNull,
    match: options.legacyValidMobile ? ["customer_phone", VALID_MOBILE_PATTERN] : null,
    ilike,
    like,
    or,
    order: [
      { column: params.sort.key, ascending: params.sort.direction === "asc", nullsFirst: false },
      // Stable tiebreaker so two pages never share or skip a row.
      { column: "customer_phone", ascending: true, nullsFirst: false },
    ],
    range: [from, from + CONTACTS_PAGE_SIZE - 1],
  };
}

/** The four roster tiles, each counting exactly what its filter lists. */
export const CONTACT_TILE_STATUSES = ["all", "opted_in", "opted_out", "invitable"] as const;
export type ContactTileStatus = (typeof CONTACT_TILE_STATUSES)[number];
export type ContactTileCounts = Record<ContactTileStatus, number>;

/** The roster plan for a tile: its status filter, no search, any source. */
export function buildTileCountQuery(status: ContactTileStatus, options: ContactsQueryOptions = {}): ContactsQueryPlan {
  return buildContactsQuery({ term: "", status, source: "all", sort: DEFAULT_CONTACTS_SORT, page: 0 }, options);
}

type ViewError = { code?: string; message?: string } | null | undefined;

/** PostgREST's "column does not exist" naming `column`: the migration adding it is not applied yet. */
export function isMissingColumnError(error: ViewError, column: string): boolean {
  return !!error && (error.code === "42703" || error.code === "PGRST204") && (error.message ?? "").includes(column);
}

/** PostgREST's "column does not exist" for is_invitable_now: the migration adding it is not applied yet. */
export function isMissingInvitableNowColumn(error: ViewError): boolean {
  return isMissingColumnError(error, INVITABLE_NOW_COLUMN);
}

/** The same error for valid_mobile: the customer_order_stats migration is not applied yet. */
export function isMissingValidMobileColumn(error: ViewError): boolean {
  return isMissingColumnError(error, VALID_MOBILE_COLUMN);
}

/** sms_contacts' marketing exclusion column (migration 20260917100000_sms_marketing_exclusion.sql). */
export const MARKETING_EXCLUDED_COLUMN = "marketing_excluded_at";

/** is_invitable_now for a row read from a view without the column. */
export function legacyInvitableNow(row: Pick<ContactListRow, "is_invitable" | "history_invite_count">): boolean {
  return row.is_invitable && (row.history_invite_count ?? 0) === 0;
}

export function hasActiveContactFilters(params: Pick<ContactsQueryParams, "term" | "status" | "source">): boolean {
  const { text, digits } = parseContactSearch(params.term);
  return params.status !== "all" || params.source !== "all" || !!text || !!digits;
}

// ---------------------------------------------------------------------------
// Owner words

export type ContactTone = "success" | "danger" | "muted";

/** "Sep 14" this year, "Sep 14, 2025" otherwise, in the store's timezone. */
export function formatContactDate(iso: string | null, timezone: string, now: Date = new Date()): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const year = (d: Date) => new Intl.DateTimeFormat("en-US", { year: "numeric", timeZone: timezone }).format(d);
  const sameYear = year(at) === year(now);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
    timeZone: timezone,
  }).format(at);
}

export interface TextClubState {
  label: string;
  tone: ContactTone;
  /** "since Sep 10" when the contact row carries the timestamp. */
  since: string | null;
}

export function describeTextClub(
  row: Pick<ContactListRow, "opt_in_status" | "opted_in_at" | "opted_out_at">,
  timezone: string,
  now: Date = new Date(),
): TextClubState {
  if (row.opt_in_status === "opted_in") {
    const at = formatContactDate(row.opted_in_at, timezone, now);
    return { label: "Subscribed", tone: "success", since: at ? `since ${at}` : null };
  }
  if (row.opt_in_status === "opted_out") {
    const at = formatContactDate(row.opted_out_at, timezone, now);
    return { label: "Opted out", tone: "danger", since: at ? `since ${at}` : null };
  }
  return { label: "Not asked", tone: "muted", since: null };
}

export const SOURCE_LABELS: Record<ContactSourceFilter, string> = {
  all: "Any source",
  orders: "Orders (POS)",
  keyword: "Texted a keyword",
  click_to_join: "Join link",
  invite: "Order invite",
  pos: "Register sign-up",
  manual: "Added by staff",
  widget: "Website sign-up",
  migration: "Text club (legacy)",
};

/** The source_ref sms_set_marketing_exclusion() stamps on a contact row it had to create. */
export const MARKETING_EXCLUSION_SOURCE_REF = "marketing_exclusion";

/** A null source means no sms_contacts row: the POS relay is the only writer of
 *  `customers`, so "Orders (POS)" is the honest origin. A row created only to
 *  hold the owner's marketing exclusion is still an orders customer. */
export function describeSource(source: ContactSource | null, sourceRef: string | null = null): string {
  if (!source) return SOURCE_LABELS.orders;
  if (source === "manual" && sourceRef === MARKETING_EXCLUSION_SOURCE_REF) return SOURCE_LABELS.orders;
  return SOURCE_LABELS[source] ?? source.replace(/_/g, " ");
}

/** The invite count every invite path uses: greatest(invite_count, history_invite_count). */
export function effectiveInviteCount(row: Pick<ContactListRow, "invite_count" | "history_invite_count">): number {
  return Math.max(row.invite_count ?? 0, row.history_invite_count ?? 0);
}

export function lastInvitedAt(
  row: Pick<ContactListRow, "last_invited_at" | "history_last_invited_at">,
): string | null {
  const a = row.last_invited_at ? Date.parse(row.last_invited_at) : NaN;
  const b = row.history_last_invited_at ? Date.parse(row.history_last_invited_at) : NaN;
  if (Number.isNaN(a) && Number.isNaN(b)) return null;
  if (Number.isNaN(b) || a >= b) return row.last_invited_at;
  return row.history_last_invited_at;
}

export interface InviteState {
  label: string;
  detail: string | null;
  tone: ContactTone | "plain";
  /** Rendered as a pill (only "Do not invite", a standing flag), otherwise text. */
  pill: boolean;
  /** Short form for the phone list's meta line; null when there is nothing to say. */
  short: string | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

export function describeInvites(
  row: Pick<
    ContactListRow,
    | "never_invite"
    | "invite_count"
    | "history_invite_count"
    | "last_invited_at"
    | "history_last_invited_at"
    | "is_invitable_now"
    | "opt_in_status"
    | "last_order_at"
  >,
  ebrWindowDays: number,
  timezone: string,
  now: Date = new Date(),
): InviteState {
  if (row.never_invite) return { label: "Do not invite", detail: null, tone: "muted", pill: true, short: "do not invite" };

  const count = effectiveInviteCount(row);
  if (count > 0) {
    const at = formatContactDate(lastInvitedAt(row), timezone, now);
    return {
      label: `${count} sent${at ? ` · ${at}` : ""}`,
      detail: row.is_invitable_now ? "eligible again" : null,
      tone: "plain",
      pill: false,
      short: at ? `invited ${at}` : "invited",
    };
  }
  if (row.is_invitable_now) return { label: "Eligible now", detail: null, tone: "success", pill: false, short: "eligible" };
  if (row.opt_in_status !== "unknown") return { label: "—", detail: null, tone: "plain", pill: false, short: null };
  if (!row.last_order_at) {
    return { label: "Not eligible", detail: "no orders on record", tone: "muted", pill: false, short: null };
  }
  const daysSince = (now.getTime() - Date.parse(row.last_order_at)) / DAY_MS;
  if (daysSince > ebrWindowDays) {
    return { label: "Not eligible", detail: `last order over ${ebrWindowDays} days ago`, tone: "muted", pill: false, short: null };
  }
  return { label: "—", detail: null, tone: "plain", pill: false, short: null };
}

// ---------------------------------------------------------------------------
// Detail sheet history

export const CONSENT_METHOD_LABELS: Record<ConsentMethod, string> = {
  keyword: "texted a keyword",
  invite_reply: "replied yes to an invite",
  click_to_join: "joined from a link",
  widget: "joined from the website",
  manual: "changed by staff",
  system_stop: "replied STOP",
  system_start: "replied START",
  migration: "imported from the old text club",
};

const MESSAGE_KIND_LABELS: Record<MessageKind, string> = {
  marketing: "Marketing text",
  invite: "Order Invite text",
  transactional: "Order text",
  system: "Automatic reply",
  test: "Test text",
  inbound: "Text",
};

export type TimelineKind = "opt_in" | "opt_out" | "consent" | "outbound" | "inbound" | "failed";

export interface TimelineEntry {
  id: string;
  at: string;
  kind: TimelineKind;
  title: string;
  detail: string | null;
}

export const TIMELINE_LIMIT = 100;

function clip(body: string, max = 80): string {
  const flat = body.replace(/\s+/g, " ").trim();
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat;
}

/** Consent events and texts merged newest first, capped at TIMELINE_LIMIT. */
export function buildContactTimeline(
  events: Pick<ConsentEvent, "id" | "to_status" | "method" | "raw_inbound" | "created_at">[],
  messages: Pick<
    SmsMessage,
    "id" | "direction" | "kind" | "body" | "status" | "error_message" | "segments" | "sent_at" | "created_at"
  >[],
): TimelineEntry[] {
  const entries: TimelineEntry[] = [];

  for (const e of events) {
    const how = CONSENT_METHOD_LABELS[e.method] ?? e.method.replace(/_/g, " ");
    const raw = e.raw_inbound ? ` · "${clip(e.raw_inbound, 40)}"` : "";
    if (e.to_status === "opted_in") {
      entries.push({ id: `c${e.id}`, at: e.created_at, kind: "opt_in", title: "Subscribed", detail: `${how}${raw}` });
    } else if (e.to_status === "opted_out") {
      entries.push({ id: `c${e.id}`, at: e.created_at, kind: "opt_out", title: "Opted out", detail: `${how}${raw}` });
    } else {
      entries.push({ id: `c${e.id}`, at: e.created_at, kind: "consent", title: "Consent reset", detail: `${how}${raw}` });
    }
  }

  for (const m of messages) {
    const at = m.sent_at ?? m.created_at;
    if (m.direction === "inbound") {
      entries.push({ id: `m${m.id}`, at, kind: "inbound", title: `Texted "${clip(m.body)}"`, detail: null });
      continue;
    }
    const label = MESSAGE_KIND_LABELS[m.kind] ?? "Text";
    const segments = m.segments ? `${m.segments} segment${m.segments === 1 ? "" : "s"}` : null;
    if (m.status === "failed" || m.status === "undelivered") {
      entries.push({ id: `m${m.id}`, at, kind: "failed", title: `${label} failed`, detail: m.error_message ?? null });
    } else if (m.status === "queued" || m.status === "sending") {
      entries.push({ id: `m${m.id}`, at, kind: "outbound", title: `${label} queued`, detail: segments });
    } else {
      const status = m.status === "delivered" ? "delivered" : null;
      entries.push({
        id: `m${m.id}`,
        at,
        kind: "outbound",
        title: `${label} sent`,
        detail: [status, segments].filter(Boolean).join(" · ") || null,
      });
    }
  }

  return entries.sort((a, b) => Date.parse(b.at) - Date.parse(a.at)).slice(0, TIMELINE_LIMIT);
}
